import React, { useMemo, useState } from 'react';
import '../styles/FrequenciaEscolar.css';
import { alunos, turmas } from '../data/escolaData.js';

const disciplinas = [
  'Português',
  'Matemática',
  'Inglês',
  'Física',
  'Química',
  'História',
  'Educação Física',
  'Geografia',
  'Artes',
  'Religião'
];

const statusOptions = [
  { id: 'presenca', label: 'Presença' },
  { id: 'falta', label: 'Falta' },
  { id: 'atestado', label: 'Atestado' }
];

const FrequenciaEscolar = () => {
  const [dataSelecionada, setDataSelecionada] = useState('');
  const [disciplinaSelecionada, setDisciplinaSelecionada] = useState('');
  const [turmaSelecionada, setTurmaSelecionada] = useState('');
  const [frequencias, setFrequencias] = useState({});
  const [registros, setRegistros] = useState([]);
  const [filtroAlunosAtivo, setFiltroAlunosAtivo] = useState(false);
  const [filtroRegistroData, setFiltroRegistroData] = useState('');
  const [filtroRegistroDisciplina, setFiltroRegistroDisciplina] = useState('');
  const [filtroRegistroTurma, setFiltroRegistroTurma] = useState('');

  const turmaOptions = turmas;

  const dataFormatada = useMemo(() => {
    if (!dataSelecionada) return '';
    const [ano, mes, dia] = dataSelecionada.split('-');
    return `${dia}/${mes}/${ano}`;
  }, [dataSelecionada]);

  const alunosVisiveis = useMemo(() => {
    if (!filtroAlunosAtivo || !turmaSelecionada) {
      return alunos;
    }
    return alunos.filter((aluno) => aluno.turmaId === turmaSelecionada);
  }, [filtroAlunosAtivo, turmaSelecionada]);

  const registrosFiltrados = useMemo(() => {
    return registros.filter((registro) => {
      const filtroDataOk = filtroRegistroData
        ? registro.data === filtroRegistroData
        : true;
      const filtroDisciplinaOk = filtroRegistroDisciplina
        ? registro.disciplina === filtroRegistroDisciplina
        : true;
      const filtroTurmaOk = filtroRegistroTurma
        ? registro.turma === filtroRegistroTurma
        : true;
      return filtroDataOk && filtroDisciplinaOk && filtroTurmaOk;
    });
  }, [filtroRegistroData, filtroRegistroDisciplina, filtroRegistroTurma, registros]);

  const handleStatusChange = (alunoId, status) => {
    setFrequencias((prev) => ({
      ...prev,
      [alunoId]: status
    }));
  };

  const limparFormulario = () => {
    setFrequencias({});
    setDisciplinaSelecionada('');
    setFiltroAlunosAtivo(false);
  };

  const handleSalvarChamada = () => {
    if (!dataSelecionada || !disciplinaSelecionada || !turmaSelecionada) {
      alert('Selecione a data, a disciplina e a turma para continuar.');
      return;
    }

    const registrosPendentes = alunosVisiveis.map((aluno) => ({
      alunoId: aluno.id,
      alunoNome: aluno.nome,
      status: frequencias[aluno.id] || 'presenca'
    }));

    const novoRegistro = {
      id: Date.now(),
      data: dataFormatada,
      disciplina: disciplinaSelecionada,
      turma: turmaOptions.find((turma) => turma.id === turmaSelecionada)?.nome,
      registros: registrosPendentes
    };

    setRegistros((prev) => [novoRegistro, ...prev]);
    limparFormulario();
    alert('Chamada registrada com sucesso!');
  };

  const handleExcluirRegistro = (id) => {
    setRegistros((prev) => prev.filter((registro) => registro.id !== id));
  };

  const exportarRegistros = (tipo) => {
    if (registrosFiltrados.length === 0) {
      alert('Não há registros para exportar com os filtros atuais.');
      return;
    }

    const exportData = registrosFiltrados.map((registro) => ({
      data: registro.data,
      disciplina: registro.disciplina,
      turma: registro.turma,
      presencas: registro.registros.filter((item) => item.status === 'presenca').length,
      faltas: registro.registros.filter((item) => item.status === 'falta').length,
      atestados: registro.registros.filter((item) => item.status === 'atestado').length
    }));

    let blob;
    let filename;

    if (tipo === 'csv') {
      const header = 'Data,Disciplina,Turma,Presencas,Faltas,Atestados';
      const rows = exportData.map((item) =>
        `${item.data},${item.disciplina},${item.turma},${item.presencas},${item.faltas},${item.atestados}`
      );
      blob = new Blob([`${header}\n${rows.join('\n')}`], { type: 'text/csv' });
      filename = 'frequencia-escolar.csv';
    } else {
      blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      filename = 'frequencia-escolar.json';
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="frequencia-escolar">
      <header className="frequencia-header">
        <h2>Frequência Escolar</h2>
        <p>Selecione a data e registre presença, falta ou atestado por aluno.</p>
      </header>

      <section className="frequencia-filtros">
        <div className="filtro-card">
          <h3>Configurar Chamada</h3>
          <div className="filtro-grid">
            <div className="form-group">
              <label htmlFor="turma">Turma</label>
              <select
                id="turma"
                value={turmaSelecionada}
                onChange={(event) => setTurmaSelecionada(event.target.value)}
              >
                <option value="">Selecione a turma</option>
                {turmaOptions.map((turma) => (
                  <option key={turma.id} value={turma.id}>
                    {turma.nome}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="data">Data</label>
              <input
                id="data"
                type="date"
                value={dataSelecionada}
                onChange={(event) => setDataSelecionada(event.target.value)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="disciplina">Disciplina</label>
              <select
                id="disciplina"
                value={disciplinaSelecionada}
                onChange={(event) => setDisciplinaSelecionada(event.target.value)}
              >
                <option value="">Selecione a disciplina</option>
                {disciplinas.map((disciplina) => (
                  <option key={disciplina} value={disciplina}>
                    {disciplina}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="filtro-actions">
            <button
              type="button"
              className="btn-secundario"
              onClick={() => setFiltroAlunosAtivo(true)}
              disabled={!turmaSelecionada}
            >
              Buscar alunos por turma
            </button>
            <button
              type="button"
              className="btn-secundario ghost"
              onClick={() => setFiltroAlunosAtivo(false)}
            >
              Limpar filtro de alunos
            </button>
          </div>
        </div>
      </section>

      <section className="frequencia-lista">
        <div className="lista-header">
          <div>
            <h3>Registro por aluno</h3>
            <p>Data: {dataFormatada || 'Selecione uma data'}</p>
          </div>
          <button
            className="btn-salvar"
            onClick={handleSalvarChamada}
            disabled={!dataSelecionada || !disciplinaSelecionada || !turmaSelecionada}
          >
            Salvar chamada
          </button>
        </div>

        <div className="lista-table">
          <table>
            <thead>
              <tr>
                <th>Aluno</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {alunosVisiveis.map((aluno) => (
                <tr key={aluno.id}>
                  <td>{aluno.nome}</td>
                  <td>
                    <div className="status-group">
                      {statusOptions.map((status) => (
                        <label key={status.id} className={`status-option status-${status.id}`}>
                          <input
                            type="radio"
                            name={`status-${aluno.id}`}
                            value={status.id}
                            checked={(frequencias[aluno.id] || 'presenca') === status.id}
                            onChange={() => handleStatusChange(aluno.id, status.id)}
                          />
                          <span>{status.label}</span>
                        </label>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="frequencia-registros">
        <div className="registros-header">
          <div>
            <h3>Últimas chamadas registradas</h3>
            <p>Filtre por data, disciplina ou turma.</p>
          </div>
          <div className="export-actions">
            <button
              type="button"
              className="btn-secundario"
              onClick={() => exportarRegistros('csv')}
            >
              Exportar CSV
            </button>
            <button
              type="button"
              className="btn-secundario ghost"
              onClick={() => exportarRegistros('json')}
            >
              Exportar JSON
            </button>
          </div>
        </div>

        <div className="registros-filtros">
          <div className="form-group">
            <label htmlFor="filtro-data">Data</label>
            <input
              id="filtro-data"
              type="text"
              placeholder="dd/mm/aaaa"
              value={filtroRegistroData}
              onChange={(event) => setFiltroRegistroData(event.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="filtro-disciplina">Disciplina</label>
            <select
              id="filtro-disciplina"
              value={filtroRegistroDisciplina}
              onChange={(event) => setFiltroRegistroDisciplina(event.target.value)}
            >
              <option value="">Todas</option>
              {disciplinas.map((disciplina) => (
                <option key={disciplina} value={disciplina}>
                  {disciplina}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="filtro-turma">Turma</label>
            <select
              id="filtro-turma"
              value={filtroRegistroTurma}
              onChange={(event) => setFiltroRegistroTurma(event.target.value)}
            >
              <option value="">Todas</option>
              {turmaOptions.map((turma) => (
                <option key={turma.id} value={turma.nome}>
                  {turma.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        {registrosFiltrados.length === 0 ? (
          <div className="empty-state">
            <p>Nenhuma chamada registrada ainda.</p>
          </div>
        ) : (
          <div className="registros-grid">
            {registrosFiltrados.map((registro) => (
              <article key={registro.id} className="registro-card">
                <div className="registro-header">
                  <h4>{registro.disciplina}</h4>
                  <span>{registro.data}</span>
                </div>
                <p className="registro-turma">{registro.turma}</p>
                <div className="registro-status">
                  {statusOptions.map((status) => {
                    const total = registro.registros.filter((item) => item.status === status.id).length;
                    return (
                      <div key={status.id} className={`status-pill status-${status.id}`}>
                        <span>{status.label}</span>
                        <strong>{total}</strong>
                      </div>
                    );
                  })}
                </div>
                <button
                  className="btn-excluir"
                  onClick={() => handleExcluirRegistro(registro.id)}
                >
                  Excluir
                </button>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="frequencia-regras">
        <h4>Regras rápidas</h4>
        <ul>
          <li>Selecione uma disciplina por chamada.</li>
          <li>O status padrão de cada aluno é Presença.</li>
          <li>Use “Atestado” para justificar ausências.</li>
        </ul>
      </section>
    </div>
  );
};

export default FrequenciaEscolar;
